import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { grecaptchaInit, grecaptchaExecute } from 'shared-utils/src/recaptcha';


import { allowWindowResize, disableWindowResize } from 'shared-utils/src/lockScreen/utils';

const emailCheck = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const pswdCheck = /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9]).{8,}$/;

const propTypes = {
  device: PropTypes.string,
  currentUrl: PropTypes.string,
  mobileOs: PropTypes.string,
  portalDomain: PropTypes.string,
  accountDomain: PropTypes.string,
  authBasePrefix: PropTypes.string,
  registerIn: PropTypes.func,
  onRegistrationComplete: PropTypes.func,
  postRegistrationAction: PropTypes.func,
  cookiesConfig: PropTypes.object,
  email: PropTypes.string,
  config: PropTypes.object,
};
const defaultProps = {
  device: null,
  currentUrl: '/',
  mobileOs: null,
  portalDomain: 'https://www.casa.it',
  accountDomain: 'https://www.casa.it',
  authBasePrefix: '/my',
  registerIn: null,
  onRegistrationComplete: null,
  postRegistrationAction: null,
  cookiesConfig: {},
  email: null,
  config: {},
};


const RegistrationLogic = ({
  accountDomain,
  authBasePrefix,
  device,
  mobileOs,
  currentUrl,
  registerIn,
  onRegistrationComplete,
  postRegistrationAction,
  config: {
    reCAPTCHA,
    cookiesConfig = {
      cookiePublisherKey: "publisherCookie",
      cookieRememberMeKey: "remember_me",
      cookieSessionName: "session",
    }, 
  },
  modalData,
  registerCallBack,
  registerCallBackPayload,
  children,
  email,
}) => {
  const isDesktop = device === 'desktop';
  const [userName, setUserName] = useState(null);
  //temp
  const [userEmail, setUserEmail] = useState(email || 'mico@mico.mi');
  const [userPwd, setUserPwd] = useState(null);
  const [userConfirmPwd, setUserConfirmPwd] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [registeringIn, setRegisteringIng] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);
  const [consent, setConsent] = useState(false);
  const [messageRegistrationError, setMessageRegistrationError] = useState('');
  const encodedCurrentUrl = encodeURIComponent(currentUrl);

  const handleRegistrationRes = (data) => {
    setRegisteringIng(false);
    if (data?.success) {
      onRegistrationComplete({
        closeModal: modalData && modalData.close,
        ...registerCallBackPayload,
        ...data,
      })

    } else {
      setRegistrationError(true);
      setMessageRegistrationError('Impossibile effettuare la registrazione: riprova più tardi')
    }
  };

  const callRecaptchaAndRegisterRef = useRef();
  
  callRecaptchaAndRegisterRef.current = useCallback(async (token) => {

    if (!validationErrors.length) {
      // username is not mandatory so we give it a default value: email without domain
      const userNameToPass = userEmail?.split('@')?.[0];
      const payload = {
        displayname: userNameToPass,
        email: userEmail,
        password: userPwd,
      };
      registerIn(
        handleRegistrationRes,
        {
          ...payload,
          app: 'prt',
          path: currentUrl,
          postRegistrationAction,
          cookiesConfig,
          gtoken: token,
        }
      );
    }

  }, [userEmail, userName, userPwd, userConfirmPwd, consent]);

  /**
  * RECAPTCHA
  */
  useEffect(() => {
    grecaptchaInit('register__captcha', reCAPTCHA.siteKeyAccount, (token) => callRecaptchaAndRegisterRef.current(token));
  }, []); 


  const storeRegistrationData = (fieldName, fieldValue) => {
    if (fieldName === 'username') {
      setUserName(fieldValue);
    } else if (fieldName === 'email') {
      setUserEmail(fieldValue);
    } else if (fieldName === 'password') {
      setUserPwd(fieldValue);
    } else if (fieldName === 'confirmPassword') {
      setUserConfirmPwd(fieldValue);
    }
  };


  const doRegistration = () => {
    if (mobileOs === 'ios') {
      disableWindowResize();
    }

    if (validationErrors.length > 0) {
      return;
    }
    if (
      (userEmail === '' || userEmail === null)
      && (userPwd === '' || userPwd === null)
      && (userConfirmPwd === '' || userConfirmPwd === null)
    ) {
      window.grecaptcha.reset();
      const errors = validationErrors.slice(0);
      if (errors.indexOf('password') < 0) {
        errors.push('password');
      }
      if (errors.indexOf('confirmPassword') < 0) {
        errors.push('confirmPassword');
      }
      if (errors.indexOf('email') < 0) {
        errors.push('email');
      }
      setValidationErrors(errors);
    } else if (userPwd === '' || userPwd === null) {
      window.grecaptcha.reset();
      const errors = validationErrors.slice(0);
      if (errors.indexOf('password') < 0) {
        errors.push('password');
      }
      setValidationErrors(errors);
    } else if (userConfirmPwd === '' || userConfirmPwd === null) {
      window.grecaptcha.reset();
      const errors = validationErrors.slice(0);
      if (errors.indexOf('confirmPassword') < 0) {
        errors.push('confirmPassword');
      }
      setValidationErrors(errors);
    } else if (userConfirmPwd !== userPwd) {
      window.grecaptcha.reset();
      const errors = validationErrors.slice(0);
      if (errors.indexOf('confirmPassword') < 0) {
        errors.push('confirmPassword');
      }

      setValidationErrors(errors);
    } else if (userEmail === '' || userEmail === null) {
      window.grecaptcha.reset();
      const errors = validationErrors.slice(0);
      if (errors.indexOf('email') < 0) {
        errors.push('email');
      }
      setValidationErrors(errors);
    } else {
      setRegisteringIng(true);
      // Removed because now we use useCallBack and useRef to call this function
      // inside the recaptcha callback
      // grecaptchaExecute('registration');
      return;
    }

  };

  const validateRegistrationData = (e) => {
    if (mobileOs === 'ios') {
      allowWindowResize();
      setTimeout(() => disableWindowResize(), 200);
    }

    const fieldName = e.target.name;
    const fieldValue = e.target.value;
    const errors = validationErrors.slice(0);
    if (fieldName === 'email') {
      if (
        fieldValue !== ''
        && !emailCheck.test(fieldValue)
      ) {
        if (errors.indexOf(fieldName) < 0) {
          errors.push(fieldName);
        }
        setValidationErrors(errors);
      }
    } else if (fieldName === 'password') {
      if (
        fieldValue !== ''
        && !pswdCheck.test(fieldValue)
      ) {
        if (errors.indexOf(fieldName) < 0) {
          errors.push(fieldName);
        }
        setValidationErrors(errors);
      }

    }
  };

  const removeError = (e) => {
    if (mobileOs === 'ios') {
      disableWindowResize();
    }

    const fieldName = e.target.name;
    if (registrationError) {
      setValidationErrors([]);
      setRegistrationError(false);
      setMessageRegistrationError('');
    } else {
      const errors = validationErrors.slice(0);
      if (errors.indexOf(fieldName) > -1) {
        errors.splice(errors.indexOf(fieldName), 1);
        setValidationErrors(errors);
        setRegistrationError(false);
        setMessageRegistrationError('');
      }
    }
  };

  return children({
    accountDomain,
    authBasePrefix,
    isDesktop,
    userName,
    userPwd,
    userConfirmPwd,
    validationErrors,
    registeringIn,
    registrationError,
    messageRegistrationError,
    encodedCurrentUrl,
    consent,
    handleRegistrationRes,
    doRegistration,
    validateRegistrationData,
    removeError,
    setUserName,
    setUserEmail,
    setUserPwd,
    setValidationErrors,
    setRegistrationError,
    setMessageRegistrationError,
    storeRegistrationData,
    setConsent,
  });
};
RegistrationLogic.propTypes = propTypes;
RegistrationLogic.defaultProps = defaultProps;
export default RegistrationLogic;